// Pure helpers for the Sources page (RGS-5): humanised `kind` labels, the
// real-series sparkline, relative "last seen", and signal-status → card tone.
// No bespoke product copy lives here beyond the kind labels (page-purpose
// nouns) — signal labels come straight from the API (see project memory
// project_rgs_signal_vocab: the short-pill/action-title catalog is deferred).

import type { SourceRow } from "@/types/vb";

/** `kind` (page purpose) → display label. `null`/unknown fold into "Other".
 *  Order of keys is the canonical Type-filter order (own-page purposes first,
 *  then external source kinds). */
export const KIND_LABELS: Record<string, string> = {
  home: "Homepage",
  about: "About",
  contact: "Contact",
  product_service: "Product/Service",
  pricing: "Pricing",
  blog_article: "Blog/Article",
  case_study: "Case study",
  team_author: "Team/Author",
  policy_legal: "Policy/Legal",
  faq_support: "FAQ/Support",
  review: "Review",
  listing: "Listing",
  social: "Social",
  press_news: "Press/News",
  reference: "Reference",
  forum_community: "Forum/Community",
  other: "Other",
};

/** Filter/display bucket for a source's kind. NULL and unknown kinds collapse to
 *  "other" so the Type filter has a single catch-all. */
export function kindKey(kind: string | null): string {
  if (kind && kind in KIND_LABELS) return kind;
  return "other";
}

export function kindLabel(kind: string | null): string {
  return KIND_LABELS[kindKey(kind)];
}

/** Canonical render order for the Type filter, intersected with the kinds
 *  actually present in this report (most reports surface only a handful). */
export function presentKindKeys(rows: SourceRow[]): string[] {
  const present = new Set(rows.map((r) => kindKey(r.kind)));
  return Object.keys(KIND_LABELS).filter((k) => present.has(k));
}

/** Build an SVG line sparkline from the real 6-point monthly series. Mirrors the
 *  mock's look (teal stroke + end dot) but plots actual counts, not a fake
 *  drift. Returns an SVG string for dangerouslySetInnerHTML-free <svg> use. */
export function sparkPath(series: number[], w: number, h: number): { d: string; cx: number; cy: number } {
  const pts = series.length ? series : [0];
  const max = Math.max(1, ...pts);
  const step = pts.length > 1 ? w / (pts.length - 1) : 0;
  const y = (v: number) => h - 2 - (v / max) * (h - 4);
  const d = pts.map((v, i) => `${i ? "L" : "M"}${(i * step).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  const cx = (pts.length - 1) * step;
  const cy = y(pts[pts.length - 1]);
  return { d, cx, cy };
}

/** ISO timestamp → "today" / "yesterday" / "N days ago" / "N weeks ago" /
 *  "N months ago". Matches the mock's recency phrasing. */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "1 week ago";
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return "1 month ago";
  return `${Math.floor(days / 30)} months ago`;
}

/** Signal health → drawer card tone class + badge label. */
export const SIGNAL_TONE: Record<string, { cls: string; badge: string }> = {
  healthy: { cls: "pass", badge: "Healthy" },
  issue: { cls: "mid", badge: "Issue" },
  critical: { cls: "fail", badge: "Critical" },
};
